import React, { useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@mui/material/CircularProgress";
import clsx from "clsx";
import { ChevronRight } from "lucide-react";
import Tooltip from "../../../Shared/Tooltip";

const ProjectOrderVisualiser = ({
  projects,
  onClick,
  disabled = false,
  loading = false,
  fullWidth = false,
  size = "medium", // 'small', 'medium', 'large'
  ...props
}) => {
  const classes = useStyles();
  const containerRef = useRef(null);

  // Enable mouse wheel vertical scroll to scroll horizontally
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (e) => {
      if (e.deltaY !== 0) {
        container.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, []);

  const sizeClass = classes[size];

  return (
    <div ref={containerRef} className={clsx(classes.root, sizeClass, fullWidth && classes.fullWidth)} onClick={onClick} disabled={disabled || loading} {...props}>
      {loading && <CircularProgress size={size === "small" ? 14 : size === "large" ? 20 : 18} thickness={5} className={classes.spinner} />}
      {!loading &&
        projects.map((project, index) => (
          <>
            <span className={classes.projectName}>{project}</span>
            {index < projects.length - 1 && <ChevronRight className={classes.chevron} />}
          </>
        ))}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    fontWeight: "normal",
    fontFamily: "Lato, sans-serif",
    border: "none",
    borderRadius: 4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start", // align left
    gap: "16px",
    color: "#0C0E29",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E0E0E0",
    transition: "background 0.3s, color 0.3s, opacity 0.3s",
    position: "relative",
    overflowX: "auto",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    scrollBehavior: "smooth",
    width: "100%",
    // Hide scrollbar for all browsers
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE 10+
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  fadeRight: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 40,
    height: "100%",
    pointerEvents: "none",
    background: "linear-gradient(to left, #fff 70%, rgba(255,255,255,0))",
    zIndex: 2,
  },
  fullWidth: {
    width: "100%",
  },
  projectName: {
    whiteSpace: "nowrap",
    width: "fit-content",
    display: "inline-block", // ensure inline
  },
  chevron: {
    height: "20px",
    width: "20px",
    minWidth: "fit-content",
    color: "#0C0E29",
    strokeWidth: "1.4px",
  },
  // Size styles
  small: {
    height: "32px",
    padding: "0 8px",
    fontSize: 16,
  },
  medium: {
    height: "40px",
    padding: "0 16px",
    fontSize: 16,
  },
  large: {
    padding: "14px 26px",
    fontSize: 18,
  },
  // Icon styles
  icon: {
    display: "inline-flex",
    alignItems: "center",
  },
  // Icon sizes
  iconSmall: {
    "& svg": {
      width: 16,
      height: 16,
    },
  },
  iconMedium: {
    "& svg": {
      width: 20,
      height: 20,
      strokeWidth: 2,
    },
  },
  iconLarge: {
    "& svg": {
      width: 24,
      height: 24,
    },
  },
  // Loading spinner
  spinner: {
    color: "#FFFFFF !important",
  },
}));

export default ProjectOrderVisualiser;
